import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Wordmark from "./Wordmark";

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.857L1.254 2.25H8.08l4.258 5.63 5.906-5.63Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

const groups = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "#features", internal: false },
      { label: "Security", href: "#security", internal: false },
      { label: "Pricing", href: "/partners", internal: true },
      { label: "Changelog", href: "#", internal: false },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "#", internal: false },
      { label: "Contact", href: "mailto:support@vawick.de", internal: false },
      { label: "Careers", href: "#", internal: false },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Documentation", href: "#", internal: false },
      { label: "Trust & GDPR", href: "/trust", internal: true },
      { label: "DPA", href: "/dpa", internal: true },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative overflow-hidden border-t border-[#0E1A1A] pt-20 sm:pt-28 lg:pt-36 pb-0 min-h-[400px] sm:min-h-[560px]">

      {/* Animated watermark background */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none select-none flex justify-center" aria-hidden="true">
        <motion.div
          className="font-display font-black whitespace-nowrap leading-[0.82]"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          style={{
            fontSize: "clamp(80px, 18vw, 210px)",
            color: "transparent",
            WebkitTextStroke: "1.5px rgba(122,155,155,0.22)",
            letterSpacing: "-0.03em",
          }}
        >
          NEXUS-VS
        </motion.div>
      </div>

      {/* Footer content sits above the watermark */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 mb-14 sm:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <Wordmark size="lg" />
            <p className="mt-5 text-sm text-[#9BB8B8] max-w-sm leading-relaxed">
              The operating system for contractor operations. Secure, intelligent, enterprise-ready.
            </p>
            <div className="mt-6 inline-flex items-center gap-2.5 text-sm font-mono text-[#9BB8B8]">
              <span
                className="w-3 h-3 rounded-full bg-[#34D399] pulse-dot shrink-0"
                style={{ boxShadow: "0 0 8px 3px rgba(52,211,153,0.55)" }}
              />
              All systems operational
            </div>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://x.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="w-8 h-8 rounded-full border border-[#152222] hover:border-[#1F3535] hover:bg-[#0D1A1A] text-[#7A9B9B] hover:text-white transition-all grid place-items-center"
              >
                <XIcon />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-8 h-8 rounded-full border border-[#152222] hover:border-[#1F3535] hover:bg-[#0D1A1A] text-[#7A9B9B] hover:text-white transition-all grid place-items-center"
              >
                <LinkedInIcon />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 grid grid-cols-3 gap-6"
          >
            {groups.map((g) => (
              <div key={g.title}>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white font-bold mb-1 pb-2 border-b border-[#152222]">{g.title}</div>
                <ul className="space-y-2.5 mt-3">
                  {g.links.map((l) => (
                    <li key={l.label}>
                      {l.internal ? (
                        <Link
                          to={l.href}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-sm text-[#9BBABA] hover:text-white transition-colors"
                        >
                          {l.label}
                        </Link>
                      ) : (
                        <a
                          href={l.href}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-sm text-[#9BBABA] hover:text-white transition-colors"
                        >
                          {l.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="flex flex-col items-center sm:flex-row sm:items-center sm:justify-between gap-3 pt-8 border-t border-[#0E1A1A] text-center sm:text-left">
          <div className="text-xs text-[#7A9B9B]">
            © {new Date().getFullYear()} nexus-vs. All rights reserved.
          </div>
          <div className="flex items-center gap-5 text-xs text-[#7A9B9B]">
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/imprint" className="hover:text-white transition">Imprint</Link>
            <button
              onClick={() => window.dispatchEvent(new Event("nexus:reopen-cookie-consent"))}
              className="hover:text-white transition"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
